/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import moment from "moment";
import DataTable from "react-data-table-component";
import Input from "../../../components/input";

/* MATERIAL TAILWIND COMPONENTS */
import { Button, Dialog, DialogBody, Tooltip } from "@material-tailwind/react";

/* ICONS */
import { FiTrash, FiEdit, FiFolderPlus, FiSave, FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { IoClose } from "react-icons/io5";

/* TOAST NOTIFICATION */
import { toast } from "react-toast";

/* API IMPORT */
import {
  deleteBlogById,
  getBlogList,
  getBlogCategories,
  addBlogCategory,
  editBlogCategoryById,
  deleteBlogCategoryById,
} from "../../../service/api";

const Blogs = () => {
  // STATE VARIABLES
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [sizePerPage, setSizePerPage] = useState(10);
  const [blogsList, setBlogsList] = useState([]);
  const [totalDocs, setTotalDocs] = useState(0);
  const [search, setSearch] = useState("");

  const [deleteId, setDeleteId] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);

  /* CATEGORY MANAGEMENT STATE WITH PAGINATION */
  const [categoryModal, setCategoryModal] = useState(false);
  const [categories, setCategories] = useState([]);
  const [catLoading, setCatLoading] = useState(false);
  const [catPage, setCatPage] = useState(1);
  const [catTotalDocs, setCatTotalDocs] = useState(0);
  const [catTotalPages, setCatTotalPages] = useState(1);
  const [catSearch, setCatSearch] = useState("");
  const [newCatName, setNewCatName] = useState("");
  const [newCatBadge, setNewCatBadge] = useState("");
  const [editingCatId, setEditingCatId] = useState(null);
  const [editCatName, setEditCatName] = useState("");
  const [editCatBadge, setEditCatBadge] = useState("");

  /* FUNCTIONS */
  const handlePageChange = (e) => {
    setPage(e);
  };

  const handlePerPageChange = (e) => {
    setSizePerPage(e);
  };

  const getBlogsListFunction = () => {
    setLoading(true);
    const params = {
      page: page,
      sizePerPage: sizePerPage,
    };
    if (search) {
      params.search = search;
    }

    getBlogList(params)
      .then((res) => {
        setBlogsList(res?.data?.docs || []);
        setTotalDocs(res?.data?.totalDocs || 0);
      })
      .catch((e) => {
        console.log("Error fetching blogs: ", e);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const fetchCategoriesFunction = (targetPage = catPage, targetSearch = catSearch) => {
    setCatLoading(true);
    const params = {
      page: targetPage,
      sizePerPage: 5,
    };
    if (targetSearch) {
      params.search = targetSearch;
    }

    getBlogCategories(params)
      .then((res) => {
        if (res?.status) {
          if (res?.data?.docs) {
            setCategories(res.data.docs);
            setCatTotalDocs(res.data.totalDocs || 0);
            setCatTotalPages(res.data.totalPages || 1);
          } else {
            setCategories(res.data || []);
            setCatTotalDocs((res.data || []).length);
            setCatTotalPages(1);
          }
        }
      })
      .catch((e) => {
        console.log("Error fetching categories:", e);
      })
      .finally(() => {
        setCatLoading(false);
      });
  };

  const handleAddCategory = (e) => {
    e.preventDefault();
    if (!newCatName.trim()) {
      toast.error("Category name is required");
      return;
    }

    setCatLoading(true);
    addBlogCategory({
      name: newCatName.trim(),
      categoryBadge: newCatBadge.trim() || "Health & Wellness",
    })
      .then((res) => {
        if (res?.status) {
          toast.success(res?.message || "Category added successfully");
          setNewCatName("");
          setNewCatBadge("");
          fetchCategoriesFunction(1, catSearch);
          setCatPage(1);
        } else {
          toast.error(res?.message || "Failed to add category");
        }
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || "Failed to add category");
      })
      .finally(() => {
        setCatLoading(false);
      });
  };

  const handleSaveEditCategory = (id) => {
    if (!editCatName.trim()) {
      toast.error("Category name cannot be empty");
      return;
    }

    setCatLoading(true);
    editBlogCategoryById(id, {
      name: editCatName.trim(),
      categoryBadge: editCatBadge.trim() || "Health & Wellness",
    })
      .then((res) => {
        if (res?.status) {
          toast.success(res?.message || "Category updated successfully");
          setEditingCatId(null);
          fetchCategoriesFunction(catPage, catSearch);
          getBlogsListFunction();
        } else {
          toast.error(res?.message || "Failed to update category");
        }
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || "Failed to update category");
      })
      .finally(() => {
        setCatLoading(false);
      });
  };

  const handleDeleteCategory = (cat) => {
    setCatLoading(true);
    deleteBlogCategoryById(cat._id)
      .then((res) => {
        if (res?.status) {
          toast.success(res?.message || `Category "${cat.name}" deleted successfully`);
          fetchCategoriesFunction(catPage, catSearch);
        } else {
          toast.error(res?.message || "Failed to delete category");
        }
      })
      .catch((err) => {
        toast.error(
          err?.response?.data?.message ||
            `Cannot delete category "${cat.name}" because it is assigned to blog articles.`
        );
      })
      .finally(() => {
        setCatLoading(false);
      });
  };

  /* HANDLE DELETE BLOG */
  const handleDelete = () => {
    setLoading(true);
    deleteBlogById(deleteId)
      .then((res) => {
        toast.success(res?.message || "Blog article deleted successfully");
      })
      .catch((e) => {
        toast.error(e?.response?.data?.message || "Failed to delete blog article");
      })
      .finally(() => {
        setDeleteModal(false);
        setDeleteId(null);
        getBlogsListFunction();
        setLoading(false);
      });
  };

  /* COLUMNS */
  const columns = [
    {
      name: "Sr. No",
      width: "70px",
      selector: (row, index) => (
        <span className="ml-2 text-sm font-ibm-semibold text-black text-opacity-60">
          {(page - 1) * sizePerPage + index + 1}
        </span>
      ),
    },
    {
      name: "Title",
      grow: 2,
      minWidth: "220px",
      wrap: true,
      selector: (row) => (
        <div className="py-2 pr-3">
          <p className="text-sm font-ibm-semibold text-black leading-snug">
            {row?.title}
          </p>
          <span className="text-xs text-greyText font-ibm-regular block mt-0.5">
            /health-blogs/{row?.slug}
          </span>
        </div>
      ),
    },
    {
      name: "Category",
      width: "160px",
      wrap: true,
      selector: (row) => (
        <div className="py-2">
          <span className="inline-block px-2.5 py-1 text-xs font-ibm-semibold rounded-full bg-logoYellow/10 text-logoYellow border border-logoYellow/20">
            {row?.category || "General"}
          </span>
          {row?.categoryBadge && (
            <span className="block text-[11px] text-greyText mt-0.5 font-ibm-regular">
              {row?.categoryBadge}
            </span>
          )}
        </div>
      ),
    },
    {
      name: "Summary",
      grow: 3,
      minWidth: "250px",
      wrap: true,
      selector: (row) => (
        <div className="py-2 pr-3">
          <span className="text-sm font-ibm-regular text-greyText leading-relaxed line-clamp-2">
            {row?.summary}
          </span>
        </div>
      ),
    },
    {
      name: "Status",
      width: "100px",
      selector: (row) => (
        <span
          className={`text-sm font-ibm-semibold ${
            row?.isActive ? "text-green-600" : "text-red-500"
          }`}
        >
          {row?.isActive ? "Active" : "Hidden"}
        </span>
      ),
    },
    {
      name: "Last Updated",
      width: "130px",
      selector: (row) => (
        <span className="text-sm font-ibm-regular text-greyText">
          {moment(row?.updatedAt).format("MMM DD, YYYY")}
        </span>
      ),
    },
    {
      name: "Actions",
      width: "100px",
      selector: (row) => (
        <div className="flex items-center gap-1">
          <Tooltip content="Edit" className="text-sm font-ibm-semibold">
            <Link to={`/blogs/edit/${row?._id}`}>
              <Button className="p-2 border border-greyBorder rounded-full shadow-none hover:shadow-none bg-transparent hover:bg-black hover:bg-opacity-5">
                <FiEdit className="w-4 h-4 text-greyText" />
              </Button>
            </Link>
          </Tooltip>
          <Tooltip content="Delete" className="text-sm font-ibm-semibold">
            <Button
              onClick={() => {
                setDeleteId(row?._id);
                setDeleteModal(true);
              }}
              className="p-2 border border-greyBorder rounded-full shadow-none hover:shadow-none bg-transparent hover:bg-black hover:bg-opacity-5"
            >
              <FiTrash className="w-4 h-4 text-greyText" />
            </Button>
          </Tooltip>
        </div>
      ),
    },
  ];

  /* USE-EFFECTS */
  useEffect(() => {
    getBlogsListFunction();
  }, [page, sizePerPage, search]);

  useEffect(() => {
    if (categoryModal) {
      fetchCategoriesFunction(catPage, catSearch);
    }
  }, [catPage, catSearch, categoryModal]);

  /* LOADING SCREEN FOR DATATABLE */
  const LoadingScreen = () => {
    return (
      <div className="w-full bg-[#F5F5F5] bg-opacity-50 rounded-md border border-authBorder py-5">
        <p className="text-sm font-ibm-semibold text-black text-center">
          Please wait!
        </p>
        <p className="text-sm font-ibm-semibold text-black text-center">
          Fetching health blogs data...
        </p>
      </div>
    );
  };

  const NoDataScreen = () => {
    return (
      <div className="w-full bg-[#F5F5F5] bg-opacity-50 rounded-md border border-authBorder py-5">
        <p className="text-sm font-ibm-semibold text-black text-center">
          There are no health blogs to display.
        </p>
      </div>
    );
  };

  return (
    <section className="px-0 py-2 xl:p-5 lg:p-5">
      {/* PAGE TITLE */}
      <Helmet>
        <title>Health Blogs | WellCare Pharmacy Admin</title>
      </Helmet>

      <div className="container-full-width">
        {/* HEADING */}
        <div className="block xl:flex lg:flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-ibm-bold text-black tracking-tight">
              Health Blogs
            </h1>
            <h2 className="text-base xl:text-lg lg:text-lg font-ibm-regular text-greyText tracking-tight">
              Add, edit, or remove health blog articles shown on the website.
            </h2>
          </div>
          <div className="block xl:flex lg:flex items-center gap-2 mt-2 xl:mt-0 lg:mt-0">
            {/* SEARCHBAR */}
            <div className="w-[30vh]">
              <Input
                type="text"
                placeholder="Search health blogs..."
                name="search"
                id="search"
                value={search}
                onChange={(e) => {
                  setSearch(e?.target?.value);
                  setPage(1);
                }}
              />
            </div>
            <Button
              onClick={() => {
                setCatPage(1);
                setCatSearch("");
                setCategoryModal(true);
              }}
              className="w-full xl:w-auto lg:w-auto whitespace-nowrap mt-2 xl:mt-0 lg:mt-0 shadow-none hover:shadow-none bg-white hover:bg-black/5 border border-authBorder px-4 py-3 rounded-md text-sm font-ibm-semibold text-black normal-case transition-all duration-300 ease-in-out flex items-center justify-center gap-2"
            >
              <FiFolderPlus className="w-4 h-4 text-greyText" />
              Manage Categories
            </Button>
            <Link to="/blogs/add">
              <Button className="w-full xl:w-auto lg:w-auto whitespace-nowrap mt-2 xl:mt-0 lg:mt-0 shadow-none hover:shadow-none bg-logoYellow hover:bg-opacity-80 px-5 py-3 rounded-md text-sm font-ibm-semibold text-white normal-case transition-all duration-300 ease-in-out">
                Add Health Blog
              </Button>
            </Link>
          </div>
        </div>

        <div className="mt-5">
          <DataTable
            className="data-table"
            columns={columns}
            data={totalDocs > 0 ? blogsList : []}
            progressPending={loading}
            progressComponent={<LoadingScreen />}
            pagination
            paginationServer
            paginationTotalRows={totalDocs}
            onChangeRowsPerPage={handlePerPageChange}
            onChangePage={handlePageChange}
            noDataComponent={<NoDataScreen />}
          />
        </div>

        {/* CATEGORY MANAGEMENT MODAL WITH PAGINATION & SEARCH */}
        <Dialog
          open={categoryModal}
          handler={() => setCategoryModal(!categoryModal)}
          size="md"
          animate={{
            mount: { scale: 1, y: 0 },
            unmount: { scale: 0.9, y: -100 },
          }}
        >
          <DialogBody className="p-0">
            <div className="border-b border-authBorder px-5 py-3 flex items-center justify-between">
              <div>
                <h5 className="text-lg font-ibm-bold text-black tracking-tight">
                  Manage Blog Categories
                </h5>
                <p className="text-xs font-ibm-regular text-greyText">
                  Add, edit spelling, or remove categories ({catTotalDocs} total).
                </p>
              </div>
              <Button
                className="p-2 rounded-full bg-white shadow-none hover:shadow-none hover:bg-black hover:bg-opacity-5"
                onClick={() => setCategoryModal(false)}
              >
                <IoClose className="w-4 h-4 text-greyText" />
              </Button>
            </div>

            {/* ADD NEW CATEGORY FORM */}
            <form onSubmit={handleAddCategory} className="p-4 border-b border-authBorder bg-gray-50/50">
              <p className="text-xs font-ibm-semibold text-black uppercase tracking-wider mb-2">
                Add New Category
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-center">
                <div className="sm:col-span-5">
                  <Input
                    type="text"
                    placeholder="Category Name (e.g. Menopause)"
                    value={newCatName}
                    onChange={(e) => setNewCatName(e.target.value)}
                  />
                </div>
                <div className="sm:col-span-5">
                  <Input
                    type="text"
                    placeholder="Badge Subtitle (e.g. Hormone Health)"
                    value={newCatBadge}
                    onChange={(e) => setNewCatBadge(e.target.value)}
                  />
                </div>
                <div className="sm:col-span-2">
                  <Button
                    type="submit"
                    disabled={catLoading}
                    className="w-full shadow-none hover:shadow-none bg-logoYellow hover:bg-opacity-80 px-3 py-3 rounded-md text-xs font-ibm-semibold text-white normal-case"
                  >
                    Add
                  </Button>
                </div>
              </div>
            </form>

            {/* CATEGORY SEARCH & LIST */}
            <div className="p-4">
              <div className="mb-3">
                <Input
                  type="text"
                  placeholder="Search categories..."
                  value={catSearch}
                  onChange={(e) => {
                    setCatSearch(e.target.value);
                    setCatPage(1);
                  }}
                />
              </div>

              <div className="max-h-[40vh] overflow-y-auto border border-authBorder rounded-md">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b border-authBorder text-xs font-ibm-semibold text-greyText uppercase bg-gray-50">
                      <th className="py-2.5 px-3">Category Name</th>
                      <th className="py-2.5 px-3">Badge Subtitle</th>
                      <th className="py-2.5 px-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-authBorder">
                    {categories.map((cat) => (
                      <tr key={cat._id} className="text-sm font-ibm-regular">
                        <td className="py-2.5 px-3">
                          {editingCatId === cat._id ? (
                            <Input
                              type="text"
                              value={editCatName}
                              onChange={(e) => setEditCatName(e.target.value)}
                            />
                          ) : (
                            <span className="font-ibm-semibold text-black">{cat.name}</span>
                          )}
                        </td>
                        <td className="py-2.5 px-3">
                          {editingCatId === cat._id ? (
                            <Input
                              type="text"
                              value={editCatBadge}
                              onChange={(e) => setEditCatBadge(e.target.value)}
                            />
                          ) : (
                            <span className="text-greyText text-xs">{cat.categoryBadge}</span>
                          )}
                        </td>
                        <td className="py-2.5 px-3 text-right">
                          {editingCatId === cat._id ? (
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                onClick={() => handleSaveEditCategory(cat._id)}
                                className="p-2 bg-green-600 hover:bg-green-700 text-white rounded-md shadow-none normal-case"
                              >
                                <FiSave className="w-4 h-4" />
                              </Button>
                              <Button
                                onClick={() => setEditingCatId(null)}
                                className="p-2 bg-gray-200 hover:bg-gray-300 text-black rounded-md shadow-none normal-case"
                              >
                                <IoClose className="w-4 h-4" />
                              </Button>
                            </div>
                          ) : (
                            <div className="flex items-center justify-end gap-1">
                              <Tooltip content="Edit Category Name">
                                <Button
                                  onClick={() => {
                                    setEditingCatId(cat._id);
                                    setEditCatName(cat.name);
                                    setEditCatBadge(cat.categoryBadge || "");
                                  }}
                                  className="p-2 border border-greyBorder rounded-full shadow-none bg-transparent hover:bg-black/5"
                                >
                                  <FiEdit className="w-3.5 h-3.5 text-greyText" />
                                </Button>
                              </Tooltip>
                              <Tooltip content="Delete Category">
                                <Button
                                  onClick={() => handleDeleteCategory(cat)}
                                  className="p-2 border border-greyBorder rounded-full shadow-none bg-transparent hover:bg-black/5"
                                >
                                  <FiTrash className="w-3.5 h-3.5 text-red-500" />
                                </Button>
                              </Tooltip>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                    {categories.length === 0 && (
                      <tr>
                        <td colSpan="3" className="py-4 text-center text-xs text-greyText font-ibm-regular">
                          No categories found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* MODAL PAGINATION FOOTER */}
            <div className="px-5 py-3 border-t border-authBorder flex items-center justify-between">
              <span className="text-xs text-greyText font-ibm-regular">
                Showing page {catPage} of {catTotalPages || 1} ({catTotalDocs} categories total)
              </span>

              <div className="flex items-center gap-2">
                <Button
                  disabled={catPage <= 1 || catLoading}
                  onClick={() => setCatPage((prev) => Math.max(prev - 1, 1))}
                  className="p-2 bg-white border border-authBorder rounded-md text-black shadow-none hover:shadow-none hover:bg-gray-100 disabled:opacity-50"
                >
                  <FiChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  disabled={catPage >= catTotalPages || catLoading}
                  onClick={() => setCatPage((prev) => Math.min(prev + 1, catTotalPages))}
                  className="p-2 bg-white border border-authBorder rounded-md text-black shadow-none hover:shadow-none hover:bg-gray-100 disabled:opacity-50"
                >
                  <FiChevronRight className="w-4 h-4" />
                </Button>
                <Button
                  onClick={() => setCategoryModal(false)}
                  className="text-xs shadow-none hover:shadow-none bg-gray-200 hover:bg-gray-300 px-4 py-2 rounded-md font-ibm-semibold text-black normal-case ml-2"
                >
                  Close
                </Button>
              </div>
            </div>
          </DialogBody>
        </Dialog>

        {/* DELETE BLOG MODAL */}
        <Dialog
          open={deleteModal}
          handler={() => setDeleteModal(!deleteModal)}
          size="xs"
          animate={{
            mount: { scale: 1, y: 0 },
            unmount: { scale: 0.9, y: -100 },
          }}
        >
          <DialogBody className="p-0">
            <div className="border-b border-authBorder px-5 py-3 flex items-center justify-between">
              <h5 className="text-lg font-ibm-bold text-black tracking-tight">
                Delete Health Blog
              </h5>
              <Button
                className="p-2 rounded-full bg-white shadow-none hover:shadow-none hover:bg-black hover:bg-opacity-5"
                onClick={() => setDeleteModal(!deleteModal)}
              >
                <IoClose className="w-4 h-4 text-greyText" />
              </Button>
            </div>
            <div className="p-5 border-b border-authBorder">
              <p className="text-base font-ibm-regular text-black">
                Are you sure you want to remove this blog article? It will no longer
                be visible on the website.
              </p>
            </div>
            <div className="flex items-center justify-end px-5 py-3">
              <Button
                onClick={handleDelete}
                className="text-sm shadow-none hover:shadow-none bg-logoYellow hover:bg-opacity-80 px-5 py-3 rounded-md font-ibm-semibold text-white transition-all duration-300 ease-in-out normal-case"
              >
                {loading ? "Please wait.." : "Confirm, delete"}
              </Button>
            </div>
          </DialogBody>
        </Dialog>
      </div>
    </section>
  );
};

export default Blogs;
